import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Button,
    Col,
    Container,
    Row,
    Tab,
    Tabs,
} from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import moment from 'moment-jalaali';
import DatePicker from 'react-datepicker2';
import { useDispatch, useSelector } from 'react-redux';

import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';
import 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css';

import { paymentActions } from '../../actions';
import PageTitle from '../../components/common/PageTitle';

const { SearchBar } = Search;

const normalizeCouponCodes = value => {
    if (Array.isArray(value)) {
        return value.reduce(
            (codes, item) => [...codes, ...normalizeCouponCodes(item)],
            [],
        );
    }

    if (value === null || value === undefined || value === '' || value === 'null') {
        return [];
    }

    return [String(value)];
};

const formatMoney = (amount, currency = 'IRR') => {
    const numericAmount = Number(amount) || 0;
    return `${numericAmount.toLocaleString('en-US')} ${currency}`;
};

const formatCurrencyTotals = totals => {
    const entries = Object.entries(totals || {});
    if (!entries.length) return '-';

    return entries
        .sort(([firstCurrency], [secondCurrency]) => firstCurrency.localeCompare(secondCurrency))
        .map(([currency, amount]) => formatMoney(amount, currency))
        .join(' | ');
};

const formatDate = value => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const capitalize = value => {
    if (!value) return '-';
    const text = String(value);
    return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
};

const normalizePayment = payment => ({
    ...payment,
    _id: payment._id || payment.id,
    currency: payment.currency || 'IRR',
    plan: `${payment.planIntervalCount || '-'} ${capitalize(payment.planInterval)}`,
    couponCodes: normalizeCouponCodes(payment.coupon),
});

const summarizePayments = (payments, groupField, getGroupValue) => {
    const groups = new Map();

    payments.forEach(payment => {
        const groupValue = getGroupValue(payment) || 'Unknown';
        const key = String(groupValue);
        const currency = payment.currency || 'IRR';
        const paymentTime = new Date(payment.createdAt).getTime();
        const existing = groups.get(key) || {
            id: `${groupField}_${key}`,
            [groupField]: groupValue,
            totalPayment: 0,
            totalsByCurrency: {},
            usedCoupon: 0,
            lastPayment: 0,
            lastPaymentCurrency: currency,
            lastPaymentDate: null,
            lastPaymentTime: Number.NEGATIVE_INFINITY,
        };

        existing.totalPayment += 1;
        existing.totalsByCurrency[currency] = (
            existing.totalsByCurrency[currency] || 0
        ) + (Number(payment.amount) || 0);
        if (payment.couponCodes.length) existing.usedCoupon += 1;

        if (paymentTime > existing.lastPaymentTime) {
            existing.lastPayment = Number(payment.amount) || 0;
            existing.lastPaymentCurrency = currency;
            existing.lastPaymentDate = payment.createdAt;
            existing.lastPaymentTime = paymentTime;
        }

        groups.set(key, existing);
    });

    return [...groups.values()].map(({ lastPaymentTime, ...group }) => group);
};

function amountFormatter(cell, row) {
    return formatMoney(cell, row.currency);
}

function totalsFormatter(cell) {
    return formatCurrencyTotals(cell);
}

function lastAmountFormatter(cell, row) {
    return formatMoney(cell, row.lastPaymentCurrency);
}

function couponFormatter(cell) {
    return cell && cell.length ? cell.join(', ') : '-';
}

function dateFormatter(cell) {
    return formatDate(cell);
}

function usernameFormatter(cell) {
    return <span className="tableUsername">{cell || '-'}</span>;
}

function customTotal(from, to, size) {
    return (
        <span className="react-bootstrap-table-pagination-total">
            Showing {from} to {to} of {size} Results
        </span>
    );
}

const paginationOptions = {
    paginationTotalRenderer: customTotal,
    showTotal: true,
    sizePerPageList: [10, 25, 50, 100],
};

const paymentColumns = [
    {
        text: 'Username',
        dataField: 'username',
        formatter: usernameFormatter,
        sort: true,
    },
    {
        text: 'Amount',
        dataField: 'amount',
        formatter: amountFormatter,
        sort: true,
    },
    {
        text: 'Plan',
        dataField: 'plan',
        sort: true,
    },
    {
        text: 'Coupon',
        dataField: 'couponCodes',
        formatter: couponFormatter,
    },
    {
        text: 'Date/Time',
        dataField: 'createdAt',
        formatter: dateFormatter,
        sort: true,
    },
];

const summaryColumns = groupField => [
    {
        text: groupField === 'user' ? 'Username' : capitalize(groupField),
        dataField: groupField,
        sort: true,
    },
    {
        text: 'Total Payments',
        dataField: 'totalPayment',
        sort: true,
    },
    {
        text: 'Total Amount',
        dataField: 'totalsByCurrency',
        formatter: totalsFormatter,
    },
    ...(groupField === 'coupon' ? [] : [{
        text: 'Used Coupon(s)',
        dataField: 'usedCoupon',
        sort: true,
    }]),
    {
        text: 'Last Amount',
        dataField: 'lastPayment',
        formatter: lastAmountFormatter,
        sort: true,
    },
    {
        text: 'Last Payment Date/Time',
        dataField: 'lastPaymentDate',
        formatter: dateFormatter,
        sort: true,
    },
];

const userColumns = summaryColumns('user');
const planColumns = summaryColumns('plan');
const couponColumns = summaryColumns('coupon');

const PaymentTable = ({
    columns,
    data,
    keyField,
}) => (
    <ToolkitProvider
        keyField={keyField}
        data={data}
        columns={columns}
        search
    >
        {props => (
            <div>
                <SearchBar {...props.searchProps} />
                <hr />
                <BootstrapTable
                    {...props.baseProps}
                    keyField={keyField}
                    pagination={paginationFactory(paginationOptions)}
                    noDataIndication="No payments found"
                    striped
                    hover
                />
            </div>
        )}
    </ToolkitProvider>
);

PaymentTable.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    keyField: PropTypes.string.isRequired,
};

const PaymentsList = () => {
    const dispatch = useDispatch();
    const loading = useSelector(state => state.payment.loading);
    const error = useSelector(state => state.payment.error);
    const payments = useSelector(state => state.payment.payments || []);
    const [startDate, setStartDate] = useState();
    const [endDate, setEndDate] = useState();
    const [appliedQuery, setAppliedQuery] = useState();

    const loadPayments = useCallback(
        query => dispatch(paymentActions.report(query)),
        [dispatch],
    );

    useEffect(() => {
        loadPayments();
    }, [loadPayments]);

    const normalizedPayments = useMemo(
        () => payments.map(normalizePayment),
        [payments],
    );
    const usersPayments = useMemo(
        () => summarizePayments(normalizedPayments, 'user', payment => payment.username),
        [normalizedPayments],
    );
    const planPayments = useMemo(
        () => summarizePayments(normalizedPayments, 'plan', payment => payment.plan),
        [normalizedPayments],
    );
    const couponPayments = useMemo(() => {
        const paymentsByCoupon = normalizedPayments.reduce(
            (rows, payment) => [
                ...rows,
                ...payment.couponCodes.map(coupon => ({ ...payment, couponGroup: coupon })),
            ],
            [],
        );

        return summarizePayments(
            paymentsByCoupon,
            'coupon',
            payment => payment.couponGroup,
        );
    }, [normalizedPayments]);

    const dateSearchHandler = () => {
        const query = {
            from: moment(startDate).startOf('day').toISOString(),
            to: moment(endDate).add(1, 'day').startOf('day').toISOString(),
        };
        setAppliedQuery(query);
        loadPayments(query);
    };

    const clearDateHandler = () => {
        setStartDate(undefined);
        setEndDate(undefined);
        setAppliedQuery(undefined);
        loadPayments();
    };

    return (
        <Container fluid className={`main-content-container px-4 ${loading ? 'blur' : ''}`}>
            <Row noGutters className="page-header py-4">
                <PageTitle
                    sm="4"
                    title="Payments List"
                    subtitle="Accounting"
                    className="text-sm-left"
                />
            </Row>

            {error && (
                <Alert variant="danger">
                    <span>{error}</span>
                    {' '}
                    <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => loadPayments(appliedQuery)}
                    >
                        Retry
                    </Button>
                </Alert>
            )}

            <Row noGutters className="dateFilterRow">
                <Col lg="2">
                    <span>Start Date:</span>
                    <DatePicker
                        id="startDate"
                        name="startDate"
                        timePicker={false}
                        onChange={setStartDate}
                        value={startDate}
                        isGregorian
                        inputFormat="YYYY/MM/DD"
                        inputJalaaliFormat="jYYYY/jMM/jDD"
                    />
                </Col>
                <Col lg="2">
                    <span>End Date:</span>
                    <DatePicker
                        name="endDate"
                        timePicker={false}
                        min={startDate ? moment(startDate) : undefined}
                        value={endDate}
                        onChange={setEndDate}
                        isGregorian
                        setTodayOnBlur={false}
                        inputFormat="YYYY/MM/DD"
                        inputJalaaliFormat="jYYYY/jMM/jDD"
                    />
                </Col>
                <Col lg="4">
                    <Button
                        disabled={!startDate || !endDate}
                        onClick={dateSearchHandler}
                    >
                        Search
                    </Button>
                    {' '}
                    <Button
                        disabled={!startDate && !endDate && !appliedQuery}
                        variant="outline-secondary"
                        onClick={clearDateHandler}
                    >
                        Clear
                    </Button>
                </Col>
            </Row>

            {loading && <p className="text-muted mt-3">Loading payments…</p>}

            <Tabs defaultActiveKey="allPayment" id="payments-tabs">
                <Tab eventKey="allPayment" title="All payments">
                    <Row>
                        <Col>
                            <PaymentTable
                                keyField="_id"
                                data={normalizedPayments}
                                columns={paymentColumns}
                            />
                        </Col>
                    </Row>
                </Tab>
                <Tab eventKey="byUsers" title="By Users">
                    <Row>
                        <Col>
                            <PaymentTable
                                keyField="user"
                                data={usersPayments}
                                columns={userColumns}
                            />
                        </Col>
                    </Row>
                </Tab>
                <Tab eventKey="byPlan" title="By Plan">
                    <Row>
                        <Col>
                            <PaymentTable
                                keyField="plan"
                                data={planPayments}
                                columns={planColumns}
                            />
                        </Col>
                    </Row>
                </Tab>
                <Tab eventKey="byCoupon" title="By Coupon">
                    <Row>
                        <Col>
                            <PaymentTable
                                keyField="coupon"
                                data={couponPayments}
                                columns={couponColumns}
                            />
                        </Col>
                    </Row>
                </Tab>
            </Tabs>
        </Container>
    );
};

export default PaymentsList;
